import { AnyAction } from "@reduxjs/toolkit";
import { Dispatch, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  RegistrationRequest,
  RegistrationResponse,
  SuccessTypeValue,
  WalletCheckboxValue,
} from "../../../types";
import {
  decodeCommonError,
  handleFailureResultObject,
  logErrorMessage,
} from "../../../utils";
import { PaymentContextValue, usePaymentContext } from "../../payment-context";
import { PaymentMethodCoordinatorJSClient } from "../clients";
import {
  setError,
  setIsLoading,
  setPaymentsClientState,
  setPaymentUuid,
} from "../state";
import { PaymentsClientContextProps } from "../payments-client-context";
import { enTextTemplates } from "../../../assets/i18n/en-text-templates";
import {
  setIsSetAsDefaultChecked,
  setIsFirstRegistrationPending,
} from "../../payment-context/state";

const createDebugIdentifiers = () => ({
  traceId: uuidv4(),
  spanId: uuidv4(),
});

export type UseCreatePaymentMethodRegistrationProps = {
  dispatch: Dispatch<AnyAction>;
  client: PaymentMethodCoordinatorJSClient | undefined;
} & Pick<
  PaymentContextValue,
  | "onFailure"
  | "onResponse"
  | "applicationName"
  | "owner"
  | "state"
  | "setWalletCheckboxProps"
> &
  Pick<PaymentsClientContextProps, "localizedText">;

export const useCreatePaymentMethodRegistration = ({
  dispatch,
  client,
  onFailure,
  onResponse,
  applicationName,
  owner,
  state,
  localizedText,
  setWalletCheckboxProps,
}: UseCreatePaymentMethodRegistrationProps): ((
  requestData: RegistrationRequest,
  retryCount?: number
) => Promise<RegistrationResponse | undefined>) => {
  const activePaymentMethod = state?.internalState?.activePaymentMethod;

  const { dispatch: contextDispatch, brand, subbrand } = usePaymentContext();

  const createPaymentMethodRegistration = useCallback(
    async (requestData: RegistrationRequest, retryCount = 2) => {
      let result = undefined;
      const debugIdentifiers = createDebugIdentifiers();
      dispatch(setPaymentUuid(debugIdentifiers));
      client?.setDebugIdentifiers(debugIdentifiers);
      const request: RegistrationRequest = {
        paymentMethod: {
          ...requestData.paymentMethod,
          owner: owner,
          source: applicationName ? { name: applicationName } : undefined,
          ...(brand ? { brand } : {}),
          ...(subbrand ? { subbrand } : {}),
        },
        ...(setWalletCheckboxProps?.defaultChecked === undefined &&
        setWalletCheckboxProps?.showWalletCheckbox ===
          WalletCheckboxValue.SET_AS_DEFAULT &&
        state.isFirstRegistrationPending
          ? {
              defaultCheck: {
                checkDefault: true,
                contractID: setWalletCheckboxProps?.contractID,
              },
            }
          : {}),
      };
      try {
        result = await client?.createPaymentMethodRegistration(
          request,
          retryCount
        );
        dispatch(setPaymentsClientState(result?.data));
        // The "Set as Default" switch will be set automatically on "ON" only if all conditions are satisfied:
        // 1. MFE shows "Set As Default" checkbox.
        // 2. The `defaultChecked` prop is not provided by the host application.
        // 3. No default payment method exists for the account.
        // 4. This is first registration call.
        if (
          setWalletCheckboxProps?.showWalletCheckbox ===
            WalletCheckboxValue.SET_AS_DEFAULT &&
          setWalletCheckboxProps?.defaultChecked === undefined &&
          state.isFirstRegistrationPending
        ) {
          contextDispatch(
            setIsSetAsDefaultChecked(!result?.data?.defaultPaymentMethodExists)
          );
        }
        onResponse({
          type: SuccessTypeValue.REGISTRATION_SUCCESS,
          registrationSuccessResponse: result?.data,
        });
        return result?.data;
      } catch (ex: any) {
        const decodedError = decodeCommonError(ex);
        const errorStatusCode = ex?.lastError?.response?.status;
        const errorObject = {
          isError: true,
          message: errorStatusCode
            ? localizedText(
                `ERROR_STATUS_CODE_${errorStatusCode}` as keyof typeof enTextTemplates
              )
            : localizedText(`REGISTRATION_RETRY_ERROR`),
        };
        dispatch(setError(errorObject));
        const failureResponse = {
          message: errorObject.message,
          decodedError: decodedError,
          rawError: ex,
        };

        onFailure(
          handleFailureResultObject(activePaymentMethod, failureResponse)
        );

        logErrorMessage(debugIdentifiers);
      } finally {
        dispatch(setIsLoading(false));
        contextDispatch(setIsFirstRegistrationPending(false));
      }
      return result?.data;
    },
    [
      brand,
      subbrand,
      client,
      contextDispatch,
      dispatch,
      onFailure,
      onResponse,
      localizedText,
      applicationName,
      owner,
      activePaymentMethod,
      setWalletCheckboxProps?.contractID,
      setWalletCheckboxProps?.defaultChecked,
      setWalletCheckboxProps?.showWalletCheckbox,
      state.isFirstRegistrationPending,
    ]
  );
  return createPaymentMethodRegistration;
};
